import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core/common/event';
import type { PermissionV2Service } from '../permission-v2-service';
import type { FeatureInitResponse, FeatureKey, UserPlan } from '../../types';
import { FeatureInitApi } from '../../apis/feature-init-api';
import { PermissionV2Model } from '../../models/permission-v2-model';
import { QueryFeaturePermissionsApi } from '../../apis/query-feature-permissions-api';
import { ShareModel } from '../../models/share-model';

@injectable()
export class PermissionV2ServiceImpl implements PermissionV2Service {
    private _onDataChangeEmitter = new Emitter<FeatureInitResponse>();
    readonly onDataChange = this._onDataChangeEmitter.event;
    private _taskPermissionRequestSequence = 0;

    constructor(
        @inject(FeatureInitApi)
        private _featureInitApi: FeatureInitApi,
        @inject(QueryFeaturePermissionsApi)
        private _queryFeaturePermissionsApi: QueryFeaturePermissionsApi,
        @inject(PermissionV2Model)
        private _model: PermissionV2Model,
        @inject(ShareModel)
        private readonly _shareModel: ShareModel,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        const data = this._model.modelData;
        if (data) {
            this._onDataChangeEmitter.fire(data);
        }
    }

    async initialize(): Promise<void> {
        const data = await this._featureInitApi.getFeatureInit();
        this._model.update(() => data);
    }

    async refreshTaskPermission(options: { taskId: string, taskType: string }): Promise<void> {
        const requestSequence = ++this._taskPermissionRequestSequence;
        try {
            const finalType = this._convert(options.taskType);
            const data = await this._queryFeaturePermissionsApi.queryFeaturePermissions({
                task_id: finalType ? options.taskId : '',
                task_type: finalType,
            });
            if (requestSequence !== this._taskPermissionRequestSequence) {
                return;
            }
            this._shareModel.update(state => {
                state.isShareModel = !data.is_owner;
                return state;
            });
        } catch (err) {
            console.warn(err);
        }
    }

    private _convert(taskType: string): string {
        const types: Record<string, string> = {
            translation: 'AI_TRANSLATION',
            'novelty-check-report': 'AI_NOVELTY_SEARCH',
            'fto-pro': 'AI_FTO_SEARCH',
            specification: 'AI_SPECIFICATION',
            disclosure: 'AI_PATENT_DISCLOSURE',
        };
        if (types[taskType]) {
            return types[taskType];
        }
        return '';
        // throw new Error(`预期外的任务类型：${taskType}`);
    }

    getFeatureData(): FeatureInitResponse | null {
        return this._model.modelData;
    }

    isAvailable(featureKey: FeatureKey): boolean {
        const data = this._model.modelData;
        if (!data) {
            return false;
        }
        return data.features[featureKey]?.isAvailable ?? false;
    }

    hasQuota(featureKey: FeatureKey): boolean {
        const data = this._model.modelData;
        if (!data) {
            return false;
        }
        const feature = data.features[featureKey];
        if (!feature) {
            return false;
        }
        return !feature.isQuotaExceeded;
    }

    getUserPlan(): UserPlan | null {
        return this._model.modelData?.userPlan ?? null;
    }

    updateFeatureData(data: FeatureInitResponse): void {
        this._model.update(() => data);
    }
}
